import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { authAPI } from '../services/api';
import { hasPermission as checkPermission, hasRole as checkRole } from '../shared/utils/permissions';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('piscicare_token');
    const savedUser = localStorage.getItem('piscicare_user');
    
    if (token && savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        setUser(userData);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Error parsing saved user:', error);
        localStorage.removeItem('piscicare_token');
        localStorage.removeItem('piscicare_user');
        setIsAuthenticated(false);
      }
    } else {
      setIsAuthenticated(false);
    }
    setLoading(false);
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      setError(null);
      const response = await authAPI.login(email, password);
      
      const { access_token, user: userData, roles, permissions } = response.data;
      
      // Merge RBAC data into the user object for easy access
      const enrichedUser = {
        ...userData,
        rbac_roles: roles || [],
        permissions: permissions || [],
      };
      
      localStorage.setItem('piscicare_token', access_token);
      localStorage.setItem('piscicare_user', JSON.stringify(enrichedUser));
      
      setUser(enrichedUser);
      setIsAuthenticated(true);
      return { success: true, user: enrichedUser };
    } catch (error) {
      let errorMessage = 'Login failed';
      
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (Array.isArray(error.response.data)) {
          errorMessage = error.response.data.map(err => 
            typeof err === 'string' ? err : err.msg || err.message || 'Validation error'
          ).join(', ');
        }
      }
      
      setError(errorMessage);
      return { 
        success: false, 
        error: errorMessage 
      };
    }
  }, []);

  const register = useCallback(async (userData) => {
    try {
      setError(null);
      const response = await authAPI.register(userData);
      return { success: true, user: response.data };
    } catch (error) {
      let errorMessage = 'Registration failed';
      
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (Array.isArray(error.response.data)) {
          errorMessage = error.response.data.map(err => 
            typeof err === 'string' ? err : err.msg || err.message || 'Validation error'
          ).join(', ');
        }
      }
      
      setError(errorMessage);
      return { 
        success: false, 
        error: errorMessage 
      };
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('piscicare_token');
    localStorage.removeItem('piscicare_user');
    setUser(null);
    setIsAuthenticated(false);
    setError(null);
  }, []);

  // Role check — delegates to the centralized permissions utility
  const hasRole = useCallback(
    (role) => checkRole(user, role),
    [user]
  );

  // Permission check — checks domain-prefixed permissions from RBAC
  const hasPermission = useCallback(
    (permission) => checkPermission(user, permission),
    [user]
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const updateUser = useCallback((updates) => {
    setUser((prev) => {
      const updated = { ...prev, ...updates };
      localStorage.setItem('piscicare_user', JSON.stringify(updated));
      return updated;
    });
  }, []);

  const value = useMemo(() => ({
    user,
    loading,
    error,
    login,
    register,
    logout,
    isAuthenticated,
    hasRole,
    hasPermission,
    clearError,
    updateUser,
  }), [user, loading, error, isAuthenticated, hasRole, hasPermission, login, register, logout, clearError, updateUser]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
